import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, TRIAL_DAYS, TRIAL_PLAN } from '../API';
import Spinner from '../Spinner/Spinner';
import '../../styles/AcceptTerms.css';
import '../../styles/ChatDrawer.css';

/**
 * ElisLockedPanel — paywalled state shown in place of ChatDrawer for non-premium users.
 *
 * Two states:
 *   - never trialed: explainer + required anonymized-data consent + "Activate free trial"
 *     CTA (Stripe Checkout for Monthly with a trial). Card collected at checkout.
 *   - trial used:    upgrade-only — single CTA to /pricing, no trial.
 *
 * Props:
 *   trialUsed: boolean — user has already consumed their one free trial
 *   onClose:   () => void — optional close handler (desktop aside; mobile panel omits)
 */
export default function ElisLockedPanel({ trialUsed = false, onClose }) {
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Local copy so a server "trial_used" response can flip the panel without a reload.
  const [usedTrial, setUsedTrial] = useState(trialUsed);

  const handleActivate = async () => {
    if (!consent || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await createCheckoutSession('subscription', {
        priceId: TRIAL_PLAN.priceId,
        withTrial: true,
        trialConsent: true,
      });
      window.location.href = res.checkoutUrl;
    } catch (err) {
      if (err?.data?.code === 'trial_used') {
        setUsedTrial(true);
      } else if (err?.status === 409) {
        setError('You already have an active subscription. Manage it from Settings.');
      } else {
        setError('Could not start your trial. Please try again.');
      }
      setSubmitting(false);
    }
  };

  const header = (
    <div className="elis-locked-header">
      <span className="elis-locked-brand">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Elis
      </span>
      {onClose && (
        <button className="elis-locked-close" onClick={onClose} aria-label="Close Elis">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  if (usedTrial) {
    return (
      <div className="elis-locked-panel">
        {header}
        <div className="elis-locked-body">
          <div className="elis-locked-icon" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="elis-locked-title">Your free trial has ended</h2>
          <p className="elis-locked-text">
            Subscribe to PrimeMaxing Premium to keep chatting with Elis about your labs,
            protocol, and assessments.
          </p>
          <button className="elis-locked-cta" onClick={() => navigate('/pricing')}>
            View plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="elis-locked-panel">
      {header}
      <div className="elis-locked-body">
        <div className="elis-locked-icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <h2 className="elis-locked-title">Meet Elis, your lab companion</h2>
        <p className="elis-locked-text">
          Elis is an AI assistant that reads your lab results, explains what they mean, and
          answers questions about your protocol and assessments in plain language.
        </p>
        <p className="elis-locked-trial">
          Start a <strong>{TRIAL_DAYS}-day free trial</strong> — then {TRIAL_PLAN.amount}
          {TRIAL_PLAN.interval}. Card required. Cancel anytime before the trial ends and you
          won&apos;t be charged.
        </p>

        <label className="accept-terms-checkbox elis-locked-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            I agree that my anonymized data may be used for testing and product improvement.
          </span>
        </label>

        {error && <p className="elis-locked-error">{error}</p>}

        <button
          className="elis-locked-cta"
          onClick={handleActivate}
          disabled={!consent || submitting}
        >
          {submitting ? <Spinner size={18} color="#fff" /> : 'Activate free trial'}
        </button>
        <button className="elis-locked-link" onClick={() => navigate('/pricing')}>
          See all plans
        </button>
      </div>
    </div>
  );
}
